/**
 * OfficePane: the REAL task pane, driven by the open Outlook message.
 *
 * When the add-in runs inside Outlook, this is what the user sees.
 *
 *   1. It looks the open message up in the case store by its `internetMessageId`
 *      (`GET /api/cases/by-message`). If the mail ingest already processed it, the
 *      pane renders that stored case (`CasePane`, with the sales reply).
 *   2. Otherwise (404 / no Message-ID) it keeps the live preview: the subject +
 *      body + sender that `office-js.ts` read from the open item are assembled into
 *      a minimal MIME locally and run through the read-only `/api/quote/preview`
 *      engine (`usePreviewQuote`) to produce the glass-box quote draft. When the engine
 *      says the mail is not an inquiry, the pane says so and shows nothing else.
 *
 * Shadow mode: it NEVER sends, saves, replies, drafts or writes anything back to the
 * mailbox, to Go Autonomous or to F&O. There is no compose/reply/send call anywhere
 * in the add-in. UI is DS-only (`@norican/react`).
 */

import { AlertTriangle, ArrowRight, Icon, Inbox, Sparkles } from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  Chip,
  DescriptionList,
  Separator,
  Spinner,
  ThemeProvider,
  Typography,
} from "@norican/react";

import { ConfidenceMeter } from "@/components/confidence-meter";
import { useAppTheme } from "@/contexts/theme";
import {
  type ApiError,
  type QuotePreview,
  type QuotePreviewLine,
  toConfidencePercent,
  usePreviewQuote,
} from "@/lib/api";
import { useCaseForMessage } from "@/lib/data";
import { intentLabel, isInScopeIntent } from "@/lib/intents";
import { logger } from "@/lib/logger";

import { CasePane, PaneHeader, PaneShadowNote } from "./case-pane";
import { type OfficeMail } from "./office-js";
import { buildMimeFromMail, extractEnquiryLines } from "./use-office-item";

type Tone = "success" | "warning" | "danger" | "neutral" | "info";

const ROUTE_TONE: Record<string, Tone> = {
  "quote-draft": "success",
  "ask-customer": "warning",
  "not-a-quote": "neutral",
};

const ROUTE_LABEL: Record<string, string> = {
  "quote-draft": "draft",
  "ask-customer": "ask customer",
  "not-a-quote": "not an inquiry",
};

const OUTCOME_TONE: Record<string, Tone> = {
  match: "success",
  clarify: "warning",
  handoff: "danger",
};

/** One resolved enquiry line inside the glass-box draft. */
function PreviewLine({ line }: { line: QuotePreviewLine }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={OUTCOME_TONE[line.outcome] ?? "neutral"} size="sm">
          {line.outcome}
        </Badge>
        {line.item && (
          <Typography as="span" variant="code" className="text-fg font-bold">
            {line.item}
          </Typography>
        )}
        {line.auto_accept && (
          <Badge tone="success" variant="outline" size="sm">
            accepted by I2Q
          </Badge>
        )}
        {line.price_chip && (
          <Chip tone="info" size="sm" className="ml-auto">
            {line.price_chip}
            {line.unit_price ? ` · ${line.unit_price} ${line.currency ?? ""}`.trimEnd() : ""}
          </Chip>
        )}
      </div>
      <span className="text-fg text-[0.8125rem] font-semibold">{line.enquiry_line}</span>
      {line.why && (
        <Typography variant="caption" tone="muted">
          {line.why}
        </Typography>
      )}
    </div>
  );
}

/** The full glass-box draft returned by the read-only preview pipeline. */
function PreviewGlassBox({ preview }: { preview: QuotePreview }) {
  const { route, intent, customer, lines = [] } = preview;

  if (intent && !isInScopeIntent(intent.intent)) {
    // Norican I2Q works on inquiries only: no customer, lines or prices for other mail.
    return (
      <Alert tone="info" title="Not an inquiry">
        I2Q reads this mail as {intentLabel(intent.intent)?.toLowerCase() ?? "other mail"}. Norican
        I2Q works on customer inquiries only, so it prepares nothing for it.
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={ROUTE_TONE[route] ?? "neutral"}>{ROUTE_LABEL[route] ?? route}</Badge>
        {typeof preview.lines_total === "number" && (
          <span className="text-fg-subtle ml-auto text-xs font-semibold">
            {preview.lines_auto ?? 0} auto · {preview.lines_total} lines
          </span>
        )}
      </div>

      {intent && (
        <div>
          <Typography variant="caption" tone="subtle" className="mb-1 block">
            Type · {intentLabel(intent.intent) ?? intent.intent}
          </Typography>
          <ConfidenceMeter value={toConfidencePercent(intent.confidence)} />
          {intent.reason && (
            <Typography variant="caption" tone="muted" className="mt-1 block">
              {intent.reason}
            </Typography>
          )}
        </div>
      )}

      {customer && (
        <DescriptionList
          orientation="horizontal"
          items={[
            {
              label: "Customer",
              value: (
                <span className="inline-flex items-center gap-1.5">
                  {customer.name ?? customer.company ?? customer.account ?? "not identified"}
                  {customer.confident ? (
                    <Badge tone="success" variant="outline" size="sm">
                      sure
                    </Badge>
                  ) : (
                    <Badge tone="warning" variant="outline" size="sm">
                      to confirm
                    </Badge>
                  )}
                </span>
              ),
            },
            ...(customer.account ? [{ label: "Account", value: customer.account }] : []),
            ...(customer.how ? [{ label: "How", value: customer.how }] : []),
          ]}
        />
      )}

      {lines.length > 0 && (
        <>
          <Separator label="Lines" />
          <div className="flex flex-col gap-3">
            {lines.map((line, index) => (
              <PreviewLine key={`${line.enquiry_line}-${index}`} line={line} />
            ))}
          </div>
        </>
      )}

      {preview.note && (
        <Typography variant="caption" tone="muted">
          {preview.note}
        </Typography>
      )}
    </div>
  );
}

export interface OfficePaneProps {
  /** The mail read from the open Outlook item (read-only). */
  mail: OfficeMail;
}

/**
 * Inside Outlook: the stored case when the mail was ingested, else the live preview.
 * A failed lookup (other than 404) also falls back to the preview, with a note.
 */
export function OfficePane({ mail }: OfficePaneProps) {
  const stored = useCaseForMessage(mail.internetMessageId);
  const { theme } = useAppTheme();

  if (stored.isLoading) {
    return (
      <ThemeProvider theme={theme} density="compact" className="w-full">
        <div className="flex items-center gap-3 p-2">
          <Spinner size="sm" label="Looking this mail up in the case store…" />
          <Typography variant="body-sm" tone="muted">
            Looking this mail up in the case store…
          </Typography>
        </div>
      </ThemeProvider>
    );
  }

  if (stored.data) {
    logger.debug("[addin] stored case found for open mail", { case: stored.data.id });
    return <CasePane detail={stored.data} />;
  }

  logger.debug("[addin] no stored case for open mail, live preview", {
    hasMessageId: mail.internetMessageId !== null,
    lookupFailed: stored.isError,
  });
  return <LivePreviewPane mail={mail} lookupFailed={stored.isError} />;
}

interface LivePreviewPaneProps extends OfficePaneProps {
  /** True when the case-store lookup failed (not a plain "not ingested"). */
  lookupFailed: boolean;
}

/** The live, read-only preview of the open mail (no stored case for it). */
function LivePreviewPane({ mail, lookupFailed }: LivePreviewPaneProps) {
  const { theme } = useAppTheme();
  const preview = usePreviewQuote();
  const enquiryLines = extractEnquiryLines(mail.body);

  const onPreview = () => {
    const mime = buildMimeFromMail(mail);
    logger.debug("[addin] read-only quote preview", { subject: mail.subject });
    preview.mutate({ mime, sender_email: mail.senderEmail ?? undefined });
  };

  const errorStatus = (preview.error as ApiError | null)?.status;
  const isUnconfigured = errorStatus === 503;
  const isOtherError = preview.isError && !isUnconfigured;

  // Compact density scoped to the narrow task pane; theme inherited from the app.
  return (
    <ThemeProvider theme={theme} density="compact" className="w-full">
      <Card variant="outline" className="overflow-hidden">
        <PaneHeader
          subtitle="not in the case store yet · live preview"
          aside={
            <Badge tone="info" variant="outline" size="sm">
              live · read-only
            </Badge>
          }
        />

        <Card.Body className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-fg-subtle inline-flex items-center gap-1.5 text-xs font-semibold">
              <Icon icon={Inbox} size="xs" />
              {mail.senderName ?? mail.senderEmail ?? "this mail"}
            </span>
            <span className="text-fg font-bold">{mail.subject || "(no subject)"}</span>
          </div>

          {lookupFailed && (
            <Typography variant="caption" tone="muted">
              The case store lookup failed, so you see a fresh preview instead of the stored case.
            </Typography>
          )}

          {enquiryLines.length > 0 && (
            <div className="flex flex-col gap-1">
              <Typography variant="caption" tone="subtle">
                {enquiryLines.length} line{enquiryLines.length === 1 ? "" : "s"} read
              </Typography>
              <ul className="text-fg-subtle flex flex-col gap-0.5 text-xs">
                {enquiryLines.slice(0, 4).map((line, index) => (
                  <li key={`${line}-${index}`} className="truncate" title={line}>
                    {line}
                  </li>
                ))}
                {enquiryLines.length > 4 && <li>+{enquiryLines.length - 4} more</li>}
              </ul>
            </div>
          )}

          <Button
            fullWidth
            tone="accent"
            startIcon={<Icon icon={Sparkles} size="sm" />}
            endIcon={<Icon icon={ArrowRight} size="sm" />}
            loading={preview.isPending}
            onClick={onPreview}
          >
            Preview the draft
          </Button>

          {isUnconfigured && (
            <Alert
              tone="warning"
              title="I2Q not configured"
              icon={<Icon icon={AlertTriangle} size="sm" />}
            >
              I2Q is not available here (503). Open the case in the browser. Nothing is written.
            </Alert>
          )}

          {isOtherError && (
            <Alert tone="danger" title="Preview failed">
              I2Q returned an error
              {errorStatus ? ` (HTTP ${errorStatus})` : ""}. Try again in a moment.
            </Alert>
          )}

          {preview.data && (
            <>
              <Separator />
              <PreviewGlassBox preview={preview.data} />
            </>
          )}

          <PaneShadowNote />
        </Card.Body>
      </Card>
    </ThemeProvider>
  );
}
